import { cn } from '@/lib/cn';

export type SegmentedTabItem<T extends string = string> = {
  id: T;
  label: string;
};

type SegmentedTabsProps<T extends string> = {
  items: SegmentedTabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  className?: string;
  /** 屏幕阅读器用 */
  ariaLabel?: string;
};

export function SegmentedTabs<T extends string>({
  items,
  value,
  onChange,
  className,
  ariaLabel = '分段切换',
}: SegmentedTabsProps<T>) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-slate-200/75 bg-gradient-to-b from-slate-100/80 via-slate-50/50 to-white/95 p-1 shadow-inner shadow-slate-900/[0.07]',
        className
      )}
      role="tablist"
      aria-label={ariaLabel}
    >
      <div className="flex max-w-full gap-0.5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:inline-flex sm:flex-wrap sm:overflow-visible">
        {items.map((t) => {
          const selected = value === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={selected}
              className={cn(
                'relative shrink-0 rounded-[0.65rem] px-3.5 py-2 text-xs font-semibold tracking-tight transition-all duration-200 sm:px-4 sm:py-2.5 sm:text-[13px]',
                selected
                  ? 'bg-gradient-to-b from-teal-500 to-teal-600 text-white shadow-md shadow-teal-900/25 ring-1 ring-white/25'
                  : 'text-slate-600 hover:bg-white/85 hover:text-slate-900 hover:shadow-sm active:scale-[0.98]'
              )}
              onClick={() => onChange(t.id)}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
